import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CallDisposition,
  CallSentiment,
  CallTask,
  CallTaskStatus,
  CompletedCallTaskStates,
  ContactList,
  DialSession,
  DialTask,
  OnboardingStatus,
  OutboundAMDState,
  ProspectProperty,
  SourceType,
  TrusthubApprovalStatus,
} from 'types/outbound';
import {
  OutboundSessionStates,
  sameTask,
} from 'browser/components/Outbound/OutboundSession';
import { RootState } from './store';
import { selectActiveUser } from './shared-selectors';
import { LBBContactPhoneNumberKind } from 'sb-lib/types';
import { getTaskPendingNumbers } from 'shared/utils/outbound';
import { getTimezoneOffset } from 'date-fns-tz';

export const initialState = {
  sessionPrepared: false,
  /* Connected Call Detail related state */
  detailTask: null as DialTask | CallTask | null,
  detailOpen: false as boolean,
  status: OutboundSessionStates.closed as OutboundSessionStates,
  callTasks: [] as CallTask[],
  preRecordedVoicemailId: null as number | null | undefined,
  sourceType: undefined as SourceType | undefined,
  sourceId: null as number | null,
  selectedNumber: null as string | null,
  approximateTaskCount: null as number | null,
  cursor: null as number | null,
  callCount: 1,
  amdState: OutboundAMDState.OFF,
  isLoadingTargets: false,
  dialSession: null as DialSession | null,
  selectedSourceLabel: '',
  muted: false,
  isResettingSource: false,
  onboardingStatus: {
    bcidTrustProductStatus: TrusthubApprovalStatus.NOT_SUBMITTED,
    businessProfileStatus: TrusthubApprovalStatus.NOT_SUBMITTED,
    cnamTrustProductStatus: TrusthubApprovalStatus.NOT_SUBMITTED,
    ssTrustProductStatus: TrusthubApprovalStatus.NOT_SUBMITTED,
    viTrustProductStatus: TrusthubApprovalStatus.NOT_SUBMITTED,
    isApolloIntegrationSetup: false,
    isHubspotIntegrationSetup: false,
    isOutreachIntegrationSetup: false,
    isSalesforceIntegrationSetup: false,
    isSalesloftIntegrationSetup: false,
    isTrusthubRegistered: false,
    isTrusthubVerified: false,
  } as OnboardingStatus,
  contactListQuery: '',
  contactListsLoaded: false,
  apolloCallDispositions: [] as CallDisposition[],
  apolloContactLists: [] as ContactList[],
  hubspotCallDispositions: [] as CallDisposition[],
  hubspotContactLists: [] as ContactList[],
  outreachCallDispositions: [] as CallDisposition[],
  outreachContactLists: [] as ContactList[],
  salesforceCallDispositions: [] as CallDisposition[],
  salesforceContactLists: [] as ContactList[],
  salesloftCallDispositions: [] as CallDisposition[],
  salesloftCallSentiments: [] as CallSentiment[],
  salesloftContactLists: [] as ContactList[],
  servicebellCallDispositions: [] as CallDisposition[],
  servicebellContactLists: [] as ContactList[],
  filters: {
    onlyOwnedTasks: true,
    timezone: [] as string[],
    numberTypes: [] as LBBContactPhoneNumberKind[],
  },
  prospectProperties: [] as ProspectProperty[],
  enforceDNC: true,
};

function addTaskNumbers(t: CallTask) {
  const phoneNumbers = t.contact?.phoneNumbers;
  if (!phoneNumbers) return t;
  const invalidNumbers = new Set([
    ...(t.invalidNumbers ?? []),
    ...phoneNumbers.filter(p => !p.isValid).map(p => p.number),
  ]);
  const attemptedNumbers = new Set([
    ...(t.attemptedNumbers ?? []),
    ...phoneNumbers
      .filter(p => !invalidNumbers.has(p.number) && p.regionCode != 'US')
      .map(p => p.number),
  ]);
  return {
    ...t,
    invalidNumbers: Array.from(invalidNumbers),
    attemptedNumbers: Array.from(attemptedNumbers),
  };
}

const outboundSlice = createSlice({
  name: 'outbound',
  initialState,
  reducers: {
    setSessionPrepared(state, action: PayloadAction<boolean>) {
      state.sessionPrepared = action.payload;
    },
    setSourceType(state, action: PayloadAction<SourceType | undefined>) {
      state.sourceType = action.payload;
    },
    setSourceId(state, action: PayloadAction<number | null>) {
      state.sourceId = action.payload;
    },
    setSelectedNumber(state, action: PayloadAction<string | null>) {
      state.selectedNumber = action.payload;
    },
    setPreRecordedVoicemailId(state, action: PayloadAction<number | null | undefined>) {
      state.preRecordedVoicemailId = action.payload;
    },
    setApproximateTaskCount(state, action: PayloadAction<number | null>) {
      state.approximateTaskCount = action.payload;
    },
    setCursor(state, action: PayloadAction<number | null>) {
      state.cursor = action.payload;
    },
    setCallCount(state, action: PayloadAction<number>) {
      state.callCount = action.payload;
      state.amdState = action.payload > 1 ? OutboundAMDState.AUTO : OutboundAMDState.OFF;
    },
    setAMDState(state, action: PayloadAction<OutboundAMDState>) {
      state.amdState = state.callCount < 2 ? action.payload : OutboundAMDState.AUTO;
    },
    setIsLoadingTargets(state, action: PayloadAction<boolean>) {
      state.isLoadingTargets = action.payload;
    },
    setDialSession(state, action: PayloadAction<DialSession | null>) {
      state.dialSession = action.payload;
    },
    setSelectedSourceLabel(state, action: PayloadAction<string>) {
      state.selectedSourceLabel = action.payload;
    },
    setMuted(state, action: PayloadAction<boolean>) {
      state.muted = action.payload;
    },
    setOnboardingStatus(state, action: PayloadAction<OnboardingStatus>) {
      state.onboardingStatus = action.payload;
    },
    setCallTasks(state, action: PayloadAction<CallTask[]>) {
      const { payload: tasks } = action;
      state.callTasks = tasks.map(addTaskNumbers);
    },
    resetCallTasks(state) {
      state.callTasks = state.callTasks.map(t => ({
        ...t,
        status: CompletedCallTaskStates.includes(t.status)
          ? t.status
          : CallTaskStatus.PENDING,
      }));
    },
    updateCallTask(state, action: PayloadAction<CallTask>) {
      const { payload: task } = action;
      state.callTasks = state.callTasks.map(t => {
        if (!sameTask(t, task)) return t;
        return { ...t, ...task };
      });
      if (state.detailTask && state.detailTask.frontendId === task.frontendId) {
        state.detailTask = { ...state.detailTask, ...task };
      }
    },
    addCallTasks(state, action: PayloadAction<CallTask[]>) {
      const { payload: tasks } = action;
      state.callTasks = [...state.callTasks, ...tasks.map(addTaskNumbers)];
    },
    setIsResettingSource(state, action: PayloadAction<boolean>) {
      state.isResettingSource = action.payload;
    },
    setContactListQuery(state, action: PayloadAction<string>) {
      state.contactListQuery = action.payload;
    },
    setContactListsLoaded(state, action: PayloadAction<boolean>) {
      state.contactListsLoaded = action.payload;
    },
    setApolloCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.apolloCallDispositions = action.payload;
    },
    setHubspotCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.hubspotCallDispositions = action.payload;
    },
    setOutreachCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.outreachCallDispositions = action.payload;
    },
    setSalesforceCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.salesforceCallDispositions = action.payload;
    },
    setSalesloftCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.salesloftCallDispositions = action.payload;
    },
    setSalesloftCallSentiments(state, action: PayloadAction<CallSentiment[]>) {
      state.salesloftCallSentiments = action.payload;
    },
    setServicebellCallDispositions(state, action: PayloadAction<CallDisposition[]>) {
      state.servicebellCallDispositions = action.payload;
    },
    setApolloContactLists(state, action: PayloadAction<ContactList[]>) {
      state.apolloContactLists = action.payload;
    },
    setHubspotContactLists(state, action: PayloadAction<ContactList[]>) {
      state.hubspotContactLists = action.payload;
    },
    setOutreachContactLists(state, action: PayloadAction<ContactList[]>) {
      state.outreachContactLists = action.payload;
    },
    setSalesforceContactLists(state, action: PayloadAction<ContactList[]>) {
      state.salesforceContactLists = action.payload;
    },
    setSalesloftContactLists(state, action: PayloadAction<ContactList[]>) {
      state.salesloftContactLists = action.payload;
    },
    setServicebellContactLists(state, action: PayloadAction<ContactList[]>) {
      state.servicebellContactLists = action.payload;
    },
    setOnlyOwnedTasks(state, action: PayloadAction<boolean>) {
      state.filters.onlyOwnedTasks = action.payload;
    },
    setTimezoneFilter(state, action: PayloadAction<string[]>) {
      state.filters.timezone = action.payload;
    },
    setPhoneNumberTypeFilter(state, action: PayloadAction<LBBContactPhoneNumberKind[]>) {
      state.filters.numberTypes = action.payload;
    },
    resetFilters(state) {
      state.filters = {
        ...initialState.filters,
        onlyOwnedTasks: state.filters.onlyOwnedTasks,
      };
    },
    setDetailTask(state, action: PayloadAction<DialTask | CallTask | null>) {
      state.detailTask = action.payload;
      state.detailOpen = !!action.payload;
    },
    setDetailOpen(state, action: PayloadAction<boolean>) {
      state.detailOpen = action.payload;
    },
    setStatus(state, action: PayloadAction<OutboundSessionStates>) {
      state.status = action.payload;
    },
    setProspectProperties(state, action: PayloadAction<ProspectProperty[]>) {
      state.prospectProperties = action.payload;
    },
    setEnforceDNC(state, action: PayloadAction<boolean>) {
      state.enforceDNC = action.payload;
    },
  },
});

export const {
  addCallTasks,
  setEnforceDNC,
  resetCallTasks,
  resetFilters,
  setAMDState,
  setApolloCallDispositions,
  setApolloContactLists,
  setApproximateTaskCount,
  setCallCount,
  setCallTasks,
  setContactListQuery,
  setContactListsLoaded,
  setCursor,
  setDetailOpen,
  setDetailTask,
  setDialSession,
  setHubspotCallDispositions,
  setHubspotContactLists,
  setIsLoadingTargets,
  setIsResettingSource,
  setMuted,
  setOnboardingStatus,
  setOnlyOwnedTasks,
  setOutreachCallDispositions,
  setOutreachContactLists,
  setPreRecordedVoicemailId,
  setSalesforceCallDispositions,
  setSalesforceContactLists,
  setSalesloftCallDispositions,
  setSalesloftCallSentiments,
  setSalesloftContactLists,
  setServicebellCallDispositions,
  setServicebellContactLists,
  setSelectedNumber,
  setSelectedSourceLabel,
  setSessionPrepared,
  setSourceId,
  setSourceType,
  setStatus,
  setTimezoneFilter,
  setPhoneNumberTypeFilter,
  setProspectProperties,
  updateCallTask,
} = outboundSlice.actions;

export default outboundSlice.reducer;

const filterContactLists = (
  contactLists: ContactList[],
  query: string,
): ContactList[] => {
  return contactLists.filter(list =>
    list.name.toLowerCase().includes(query.toLowerCase()),
  );
};

export const selectFilteredContactLists = createSelector(
  (state: RootState) => state.outbound.contactListQuery,
  (state: RootState) => state.outbound.apolloContactLists,
  (state: RootState) => state.outbound.hubspotContactLists,
  (state: RootState) => state.outbound.outreachContactLists,
  (state: RootState) => state.outbound.salesforceContactLists,
  (state: RootState) => state.outbound.salesloftContactLists,
  (state: RootState) => state.outbound.servicebellContactLists,
  (
    query,
    apolloLists,
    hubspotLists,
    outreachLists,
    salesforceLists,
    salesloftLists,
    servicebellLists,
  ) => {
    return {
      filteredApolloContactLists: filterContactLists(apolloLists, query),
      filteredHubspotContactLists: filterContactLists(hubspotLists, query),
      filteredOutreachContactLists: filterContactLists(outreachLists, query),
      filteredSalesforceContactLists: filterContactLists(salesforceLists, query),
      filteredSalesloftContactLists: filterContactLists(salesloftLists, query),
      filteredServicebellContactLists: filterContactLists(servicebellLists, query),
    };
  },
);

export const selectCallDispositions = createSelector(
  (state: RootState) => state.outbound.detailTask,
  (state: RootState) => state.outbound.apolloCallDispositions,
  (state: RootState) => state.outbound.hubspotCallDispositions,
  (state: RootState) => state.outbound.outreachCallDispositions,
  (state: RootState) => state.outbound.salesforceCallDispositions,
  (state: RootState) => state.outbound.salesloftCallDispositions,
  (state: RootState) => state.outbound.servicebellCallDispositions,
  (
    task,
    apolloDispositions,
    hubspotDispositions,
    outreachDispositions,
    salesforceDispositions,
    salesloftDispositions,
    servicebellDispositions,
  ) => {
    switch (task?.sourceType) {
      case SourceType.APOLLO_SEQUENCE:
      case SourceType.APOLLO_TASK:
        return apolloDispositions;
      case SourceType.HUBSPOT_LIST:
      case SourceType.HUBSPOT_TASK:
        return hubspotDispositions;
      case SourceType.OUTREACH_SEQUENCE:
      case SourceType.OUTREACH_TASK:
        return outreachDispositions;
      case SourceType.SALESFORCE_REPORT:
        return salesforceDispositions;
      case SourceType.SALESLOFT_CADENCE:
      case SourceType.SALESLOFT_TASK:
        return salesloftDispositions;
      case SourceType.SERVICEBELL_LIST:
        return servicebellDispositions;
      default:
        // For a CONTACT or a DIALPAD source, fall back to any
        // dispositions that we might have available.
        for (const dispositions of [
          apolloDispositions,
          hubspotDispositions,
          outreachDispositions,
          salesforceDispositions,
          salesloftDispositions,
          servicebellDispositions,
        ]) {
          if (dispositions.length > 0) {
            return dispositions;
          }
        }
        return [];
    }
  },
);

export const selectCallSentiments = createSelector(
  (state: RootState) => state.outbound.detailTask,
  (state: RootState) => state.outbound.salesloftCallSentiments,
  (task, salesloftCallSentiments) => {
    switch (task?.sourceType) {
      case SourceType.SALESLOFT_CADENCE:
      case SourceType.SALESLOFT_TASK:
        return salesloftCallSentiments;
      case SourceType.CONTACT:
      case SourceType.DIALPAD:
        // For a CONTACT or a DIALPAD source, fall back to any
        // dispositions that we might have available.
        for (const sentiments of [salesloftCallSentiments]) {
          if (sentiments.length > 0) {
            return sentiments;
          }
        }
        return [];
      default:
        return [];
    }
  },
);

export const selectFilteredCallTasks = createSelector(
  (state: RootState) => selectActiveUser(state),
  (state: RootState) => state.outbound.callTasks,
  (state: RootState) => state.outbound.filters.onlyOwnedTasks,
  (state: RootState) => state.outbound.filters.timezone,
  (state: RootState) => state.outbound.filters.numberTypes,
  (state: RootState) => state.outbound.enforceDNC,
  (user, tasks, onlyOwned, timezone, numberTypes, enforceDNC) => {
    if (onlyOwned) {
      tasks = tasks.filter(t => t.userId === user?.id);
    }

    if (timezone.length > 0) {
      const filterTimezoneNames = timezone.map(tz => tz.toLowerCase());
      const filterTimezoneOffsets = filterTimezoneNames.map(tz => getTimezoneOffset(tz));

      tasks = tasks.filter(t => {
        const taskTimezoneName = t?.contact?.timezone?.toLowerCase() ?? '';
        const taskTimezoneOffset = getTimezoneOffset(taskTimezoneName);
        return filterTimezoneOffsets.includes(taskTimezoneOffset);
      });
    }

    if (numberTypes.length > 0) {
      tasks = tasks.map(t => {
        let attemptedNumbers = t.attemptedNumbers ?? [];
        for (const taskNumber of t.contact?.phoneNumbers ?? []) {
          if (
            !numberTypes.includes(taskNumber.kind) &&
            !attemptedNumbers.includes(taskNumber.number)
          ) {
            attemptedNumbers = [...attemptedNumbers, taskNumber.number];
          }
        }
        return {
          ...t,
          attemptedNumbers,
        };
      });
    }

    if (enforceDNC) {
      tasks = tasks.map(t => {
        const phoneNumbers = t.contact?.phoneNumbers;
        if (!phoneNumbers) return t;
        const invalidNumbers = t.invalidNumbers ?? [];
        const attemptedNumbers = new Set([
          ...(t.attemptedNumbers ?? []),
          ...phoneNumbers
            .filter(p => !invalidNumbers.includes(p.number) && p.doNotCall)
            .map(p => p.number),
        ]);
        return {
          ...t,
          attemptedNumbers: Array.from(attemptedNumbers),
        };
      });
    }

    return tasks.map(t => {
      return t.status === CallTaskStatus.PENDING && getTaskPendingNumbers(t).length === 0
        ? { ...t, status: CallTaskStatus.BAD_NUMBER }
        : t;
    });
  },
);
